import type { AdvertisingChannel } from "@/api/advertising";
import type { BuyerLeadSource } from "@/api/buyers";

/**
 * Which `BuyerLeadSource` an advertisement's channel implies — the rule the
 * intake forms use to DEFAULT a lead's Source when a specialist picks an
 * advertisement (§2.2, Q6).
 *
 * 🔴 A MIRROR of the C# constant `AdvertisingChannelSources`, held in step by
 * the shared fixture `tests/fixtures/advertising-channel-sources.json`, which
 * `advertisingChannelSources.test.ts` here and
 * `AdvertisingChannelSourcesParityTests` on the server both read. Nothing
 * imports across the language boundary, so the fixture is what stops the two
 * drifting. Edit all three together.
 *
 * It is a DEFAULT, never a lock: the source stays editable after it is
 * filled. The advertisement itself is the attribution (D2 retired the
 * campaign code).
 *
 * Typed as an exhaustive `Record` over the channel union so a channel added to
 * `AdvertisingChannel` without an entry here is a typecheck error, not a
 * silent `undefined`.
 */
export const ADVERTISING_CHANNEL_SOURCES: Record<AdvertisingChannel, BuyerLeadSource> = {
  // The four listing sites name the PAGE the lead came off, not a paid channel.
  BizBuySell: "ListingSite",
  BizQuest: "ListingSite",
  LoopNet: "ListingSite",
  BusinessesForSale: "ListingSite",
  GoogleSearch: "PaidSearch",
  Meta: "PaidSocial",
  YouTube: "PaidSocial",
  LinkedIn: "PaidSocial",
  Email: "InboundEmail",
  DirectMail: "Other",
  Event: "Event",
  Other: "Other",
};

/** The source a channel implies, or `"Unknown"` for a token the map does not
 *  know — "nobody has said", never `"Other"`, which is a real channel a manager
 *  chose. Mirrors `AdvertisingChannelSources.SourceFor`. */
export function sourceForChannel(channel: string | null | undefined): BuyerLeadSource {
  if (!channel) return "Unknown";
  return (ADVERTISING_CHANNEL_SOURCES as Record<string, BuyerLeadSource | undefined>)[channel]
    ?? "Unknown";
}
